/**
 * Contacts other people's systems reported, drawn so nobody mistakes them for
 * ours and nobody mistakes an unknown for a friend.
 *
 * Five rules, each of which exists because the alternative is a specific lie on
 * a shared map.
 *
 * **Affiliation drives the colour and the letter, and UNKNOWN is never blue.**
 * The single most dangerous bug available in this feature is a stranger's
 * unidentified contact drawn in the friendly colour: somebody flies near it,
 * does not engage it, and trusts a marker a feed they have never audited put on
 * the screen. So the colour lookup has no friendly fallback — an affiliation
 * this build does not recognise resolves to UNKNOWN, never to FRIEND — and the
 * hue is backed by a 2525-style letter (F/H/N/U) so the distinction survives a
 * colour-blind operator, a washed-out panel in daylight and a monochrome print.
 *
 * **External contacts carry a frame ours never have.** A double ring, an
 * affiliation letter and an `EXT` prefix on the label. Our own aircraft are DOM
 * markers with a heading arrow and a bare callsign (see `map/icons.ts`), so the
 * two are different at a glance, in shape as well as in text.
 *
 * **Staleness is on the symbol, not only in a panel.** A contact that has
 * outlived its freshness dims and its label says when it was last heard and
 * from whom. A panel an operator has not opened is not a warning.
 *
 * **A dead feed is a fact about the feed.** Every contact from a source that is
 * down or cut reads "SOURCE OFFLINE — <feed>", together, in one colour. Two
 * hundred contacts each independently going stale reads to an operator as a
 * mass event in the world rather than a broken cable, and that is the failure
 * `docs/INTEGRATION-LAYER.md` §5 exists to prevent.
 *
 * **Provenance is one hover away.** Who said it, when they say it was true
 * versus when it reached us, on whose scale, and whether they were relaying it
 * from somebody else. A marker the operator cannot interrogate is a dot.
 *
 * Colours are hex, not the `--friend` / `--unknown` CSS tokens they mirror.
 * These values ride on GeoJSON features into MapLibre paint properties, and
 * MapLibre's colour parser understands hex/rgb/hsl and NOT oklch — an oklch
 * value there logs "Could not parse color" per feature and the contact never
 * draws at all. `map/icons.ts` records the same trap.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { bindHoverLayer } from '@/map/hoverLayer';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import {
  externalActions,
  subscribeToExternalPicture,
  useExternalStore,
  type ExternalContact,
  type TrackLifecycle,
} from '@/stores/externalStore';
import { useMapStore } from '@/stores/mapStore';

export const EXTERNAL_TRACK_SOURCE_ID = 'external-tracks';

/**
 * Whether a contact's feed kind is switched on in the LAYERS tab.
 *
 * The switches suppress named kinds rather than allowlisting them, and that
 * distinction is the whole design. An allowlist would make every contact from a
 * feed nobody thought to add a switch for — a REST-pulled partner, a camera
 * detection — vanish from the map the moment both boxes were unticked, and the
 * operator would have no way to tell that from a quiet sky.
 *
 * Matched on the source's adapter kind (`cot-udp`, `adsb-json`, `detector`)
 * because that is what actually produced the contact. A CoT event carrying an
 * aircraft is still CoT: switching off ADS-B must not hide it, or "ADS-B off"
 * would silently mean "some of the tracks somebody else told us about, off".
 */
export function visibleKind(contact: ExternalContact): boolean {
  const kind = contact.track.source?.sourceType ?? '';
  const { adsb, cot } = useMapStore.getState().layers;
  if (kind.startsWith('adsb')) return adsb;
  if (kind.startsWith('cot')) return cot;
  return true;
}
/** Exported: the hover affordance hit-tests this layer, and so do the tests. */
export const EXTERNAL_TRACK_HALO_LAYER = 'external-tracks-halo';
const EXTERNAL_TRACK_FRAME_LAYER = 'external-tracks-frame';
const EXTERNAL_TRACK_SYMBOL_LAYER = 'external-tracks-symbol';
const EXTERNAL_TRACK_LABEL_LAYER = 'external-tracks-label';
const EXTERNAL_TRACK_STATUS_LAYER = 'external-tracks-status';

const EMPTY = { type: 'FeatureCollection', features: [] as unknown[] };

/**
 * The prefix every external contact's on-map label carries.
 *
 * Not decoration: it is the piece of the distinction that survives being read
 * aloud over a radio, where a ring and a hue do not.
 */
export const EXTERNAL_LABEL_PREFIX = 'EXT';

/**
 * What an onboard detector's contact is prefixed with instead.
 *
 * A camera's guess and another headquarters' considered picture deserve
 * different scepticism, and over a radio "DET truck" and "EXT truck" are the
 * shortest way to say which one is being looked at. A detection is a machine's
 * opinion about pixels, projected onto ground that was assumed flat.
 */
export const DETECTION_LABEL_PREFIX = 'DET';

/**
 * The affiliation palette, mirroring `--friend` / `--hostile` / `--neutral` /
 * `--unknown` in `styles.css` as hex for the reason in the module note.
 */
export const AFFILIATION_COLOR = {
  friend: '#4fa6e9',
  hostile: '#ed4042',
  neutral: '#65c86b',
  unknown: '#e6c540',
} as const;

/** MIL-STD-2525's identity letters, which is how this reads on paper anyway. */
export const AFFILIATION_SYMBOL = {
  friend: 'F',
  hostile: 'H',
  neutral: 'N',
  unknown: 'U',
} as const;

const AFFILIATION_LABEL = {
  friend: 'FRIEND',
  hostile: 'HOSTILE',
  neutral: 'NEUTRAL',
  unknown: 'UNKNOWN',
} as const;

type KnownAffiliation = keyof typeof AFFILIATION_COLOR;

function isKnown(value: string): value is KnownAffiliation {
  return value === 'friend' || value === 'hostile' || value === 'neutral' || value === 'unknown';
}

/**
 * The colour for an affiliation.
 *
 * The fallback is UNKNOWN and it is the whole point of the function existing.
 * A `Record` lookup with `?? FRIEND`, or a default parameter of `friend`, would
 * paint every contact this build cannot classify in the one colour that means
 * "ours" — and a value this build cannot classify is by definition one nobody
 * has vouched for.
 */
export function affiliationColor(affiliation: string): string {
  return isKnown(affiliation) ? AFFILIATION_COLOR[affiliation] : AFFILIATION_COLOR.unknown;
}

export function affiliationSymbol(affiliation: string): string {
  return isKnown(affiliation) ? AFFILIATION_SYMBOL[affiliation] : AFFILIATION_SYMBOL.unknown;
}

export function affiliationLabel(affiliation: string): string {
  return isKnown(affiliation) ? AFFILIATION_LABEL[affiliation] : AFFILIATION_LABEL.unknown;
}

/**
 * The colour a lifecycle line is written in.
 *
 * A dead feed is amber rather than the affiliation's own hue, so a screenful of
 * "SOURCE OFFLINE" reads as one event with one cause instead of two hundred
 * separate contacts each having their own problem.
 */
const STATUS_COLOR: Record<TrackLifecycle, string> = {
  live: 'rgba(244,244,245,0.55)',
  'no-longer-reported': '#e6c540',
  'retired-by-source': 'rgba(244,244,245,0.7)',
  'source-down': '#f0a53f',
  superseded: '#7fb6ff',
};

/**
 * How solid the contact is drawn.
 *
 * A departed contact is a record of a removal, not a claim that something is
 * there, so it is faint. A contact from a dead feed keeps most of its weight:
 * it is still the last thing anybody said about that object, and fading it out
 * would suggest the object went away rather than the cable.
 */
const LIFECYCLE_OPACITY: Record<TrackLifecycle, number> = {
  live: 0.95,
  'no-longer-reported': 0.5,
  'retired-by-source': 0.3,
  'source-down': 0.75,
  superseded: 0.3,
};

/** Seconds as an operator says them out loud. */
export function formatAge(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  if (whole < 60) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * A timestamp as UTC clock time, or a dash.
 *
 * Guarded because these numbers crossed the trust boundary. `toISOString`
 * throws on a non-finite date, and a throw inside the redraw would take every
 * *other* contact off the map with it — one hostile feed erasing the picture is
 * exactly the dependency §2 says an input must never become.
 */
function clockTime(unixSeconds: number): string {
  if (!Number.isFinite(unixSeconds)) return '—';
  const at = new Date(unixSeconds * 1000);
  return Number.isNaN(at.getTime()) ? '—' : `${at.toISOString().slice(11, 19)}Z`;
}

/**
 * The line under the symbol: what became of this contact, in words.
 *
 * Each lifecycle gets a different sentence and a different colour, because the
 * four facts §5 names would otherwise render identically — and rendering them
 * identically is the whole failure. A removal always names the feed responsible
 * so the operator can act on the feed rather than on the map.
 */
export function statusTextFor(contact: ExternalContact, now: number): string {
  const feed = contact.track.source.sourceId;
  const lastHeard = contact.track.observedAt === null ? null : now - contact.track.observedAt;

  switch (contact.lifecycle) {
    case 'source-down':
      return contact.sourceDownReason === 'cut-by-operator'
        ? `FEED CUT · ${feed}`
        : `SOURCE OFFLINE · ${feed}`;
    case 'retired-by-source':
      return `RETIRED BY ${feed}`;
    case 'superseded':
      return `NOW FROM ${contact.supersededBy ?? 'another feed'}`;
    case 'no-longer-reported':
      return lastHeard === null
        ? `NEVER TIMED · ${feed}`
        : `LAST HEARD ${formatAge(lastHeard)} AGO · ${feed}`;
    case 'live':
      return feed;
  }
}

/** The on-map label. Prefixed so an external contact is never read as ours. */
export function labelFor(contact: ExternalContact): string {
  return `${labelPrefixFor(contact)} ${contact.track.callsign}`;
}

/**
 * DET for a camera's guess, EXT for everything else somebody told us.
 *
 * The comparison is against `'detection'` rather than `!== 'external'` on
 * purpose: a deployment mid-upgrade sends no kind at all, and the negative
 * form would dress every one of those partner contacts up as something this
 * system saw for itself.
 */
export function labelPrefixFor(contact: ExternalContact): string {
  return contact.track.trackSource === 'detection'
    ? DETECTION_LABEL_PREFIX
    : EXTERNAL_LABEL_PREFIX;
}

export function externalContactFeature(contact: ExternalContact, now: number): unknown {
  const { track } = contact;
  const color = affiliationColor(track.affiliation);
  return {
    type: 'Feature',
    properties: {
      trackId: track.trackId,
      sourceId: track.source.sourceId,
      sourceTrackId: track.source.sourceTrackId,
      callsign: track.callsign,
      affiliation: track.affiliation,
      color,
      symbol: affiliationSymbol(track.affiliation),
      lifecycle: contact.lifecycle,
      label: labelFor(contact),
      status: statusTextFor(contact, now),
      statusColor: STATUS_COLOR[contact.lifecycle],
      // Read by the tests and by anything downstream that has to answer "is
      // this one of ours?" without parsing a label.
      external: true,
      present: contact.present,
      stale: track.stale,
      opacity: LIFECYCLE_OPACITY[contact.lifecycle],
      // UNKNOWN is drawn heaviest. Where the picture is least certain is where
      // the operator most needs to notice it, and a thin ring reads as
      // background.
      strokeWidth: track.affiliation === 'unknown' ? 3 : 2,
      radius: track.affiliation === 'unknown' ? 10 : 8,
    },
    geometry: { type: 'Point', coordinates: [track.lon, track.lat] },
  };
}

/**
 * Everything one source said about one contact, verbatim.
 *
 * Their timestamp and ours are both shown and never collapsed into one: a
 * source whose clock is hours out would otherwise make its contacts either
 * permanently fresh or permanently stale, silently.
 */
export function externalHoverLines(contact: ExternalContact, now: number): string[] {
  const { track } = contact;
  const p = track.source;
  const lines: string[] = [
    `${track.lat.toFixed(5)}, ${track.lon.toFixed(5)}`,
    statusTextFor(contact, now),
    `SOURCE ${p.sourceId}${p.adapterKind ? ` · ${p.adapterKind}` : ''}`,
    `THEIR ID ${p.sourceTrackId}`,
  ];

  lines.push(
    p.observedAt === null
      ? 'OBSERVED not stated by the source'
      : `OBSERVED ${clockTime(p.observedAt)} · ${formatAge(now - p.observedAt)} ago`,
  );
  lines.push(`RECEIVED ${clockTime(p.receivedAt)} · ${formatAge(now - p.receivedAt)} ago`);

  // Their number on their scale, or a plain statement that there is none.
  // Anything else here would be a cross-source score, and two vendors' "0.8"
  // do not mean the same thing — §4 forbids inventing that comparability.
  lines.push(
    typeof p.sourceConfidence === 'number'
      ? `CONFIDENCE ${p.sourceConfidence} on ${p.sourceId}'s own scale`
      : 'CONFIDENCE none carried by this feed',
  );

  if (p.sourceType) lines.push(`THEIR TYPE ${p.sourceType}`);
  if (p.how) lines.push(`HOW ${p.how}`);
  if (p.relayed) lines.push('RELAYED — they had it from somewhere else');
  if (track.positionErrorM !== null) {
    // The accuracy travels with the position it describes and names the same
    // source. Mixing one feed's accuracy with another's position draws a
    // certainty circle around a point nobody measured.
    lines.push(`ACCURACY ±${Math.round(track.positionErrorM)} m per ${p.sourceId}`);
  }
  if (track.altM !== null) lines.push(`ALT ${Math.round(track.altM)} m`);
  if (track.courseDeg !== null && track.speedMs !== null) {
    lines.push(`${Math.round(track.courseDeg)}° at ${track.speedMs.toFixed(1)} m/s`);
  }
  if (track.remarks) lines.push(track.remarks);
  if (track.mergedInto !== null) {
    // This marker and at least one other are believed to be one object, and
    // this list is the audit view — one row per report, on purpose. Saying so
    // here is what stops an operator reconciling by hand a duplicate the
    // picture already reconciled and never mentioned.
    lines.push('MERGED — the picture shows this as one contact with another report');
  }
  return lines;
}

export interface ExternalTrackHover {
  /** Map-container pixel coordinates of the pointer. */
  x: number;
  y: number;
  /** e.g. "EXT · UNKNOWN · N123AB". */
  title: string;
  lines: string[];
  color: string;
}

/** The properties MapLibre hands back on a hovered feature. */
interface HoverEvent {
  features?: Array<{ properties: Record<string, unknown> | null } | undefined>;
  point: { x: number; y: number };
}

export function hoverFromProperties(
  properties: Record<string, unknown>,
  point: { x: number; y: number },
): ExternalTrackHover | null {
  const trackId = properties.trackId;
  if (typeof trackId !== 'string') return null;

  const affiliation = typeof properties.affiliation === 'string' ? properties.affiliation : '';
  const callsign = typeof properties.callsign === 'string' ? properties.callsign : trackId;

  // MapLibre flattens feature properties to primitives, so the line array
  // travels as JSON. A malformed value must not take the map down with it.
  let lines: string[] = [];
  if (typeof properties.lines === 'string') {
    try {
      const parsed: unknown = JSON.parse(properties.lines);
      if (Array.isArray(parsed)) lines = parsed.filter((l): l is string => typeof l === 'string');
    } catch {
      lines = [];
    }
  }

  return {
    x: point.x,
    y: point.y,
    title: `${EXTERNAL_LABEL_PREFIX} · ${affiliationLabel(affiliation)} · ${callsign}`,
    lines,
    // Never the raw property with a friendly fallback: an unparseable colour
    // must land on UNKNOWN like everything else this build cannot classify.
    color: affiliationColor(affiliation),
  };
}

export interface ExternalTrackLayerOptions {
  onHover?: (hover: ExternalTrackHover | null) => void;
  /** Injectable clock, so the age on a label can be asserted without waiting. */
  now?: () => number;
}

export interface ExternalTrackLayer {
  destroy: () => void;
  stats: () => { redraws: number; contacts: number };
  /** Redraw the contacts already held, for when a LAYERS switch changes. */
  redraw: () => void;
}

export function createExternalTrackLayer(
  map: MapLike,
  options: ExternalTrackLayerOptions = {},
): ExternalTrackLayer {
  let redraws = 0;
  let drawn = 0;
  const clock = options.now ?? (() => Date.now() / 1000);

  if (!map.getSource(EXTERNAL_TRACK_SOURCE_ID)) {
    map.addSource(EXTERNAL_TRACK_SOURCE_ID, { type: 'geojson', data: EMPTY });
  }

  // The outer ring is the "somebody else reported this" frame. Nothing of ours
  // wears one — our aircraft are DOM markers with a heading arrow — so the
  // difference survives at a glance, before any label is read.
  if (!map.getLayer(EXTERNAL_TRACK_FRAME_LAYER)) {
    map.addLayer({
      id: EXTERNAL_TRACK_FRAME_LAYER,
      type: 'circle',
      source: EXTERNAL_TRACK_SOURCE_ID,
      paint: {
        'circle-radius': ['+', ['get', 'radius'], 5],
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 1,
        'circle-stroke-opacity': ['*', ['get', 'opacity'], 0.55],
      },
    });
  }

  if (!map.getLayer(EXTERNAL_TRACK_HALO_LAYER)) {
    map.addLayer({
      id: EXTERNAL_TRACK_HALO_LAYER,
      type: 'circle',
      source: EXTERNAL_TRACK_SOURCE_ID,
      paint: {
        'circle-radius': ['get', 'radius'],
        // Darkened middle rather than a filled disc: the affiliation letter
        // has to stay legible over bright imagery without hiding the ground.
        'circle-color': 'rgba(0,0,0,0.32)',
        'circle-opacity': ['get', 'opacity'],
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': ['get', 'strokeWidth'],
        'circle-stroke-opacity': ['get', 'opacity'],
      },
    });
  }

  // The identity letter, inside the ring. The redundant channel that makes the
  // friend/unknown distinction survive a colour-blind operator.
  if (!map.getLayer(EXTERNAL_TRACK_SYMBOL_LAYER)) {
    map.addLayer({
      id: EXTERNAL_TRACK_SYMBOL_LAYER,
      type: 'symbol',
      source: EXTERNAL_TRACK_SOURCE_ID,
      layout: {
        'text-field': ['get', 'symbol'],
        'text-size': 11,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-opacity': ['get', 'opacity'],
        'text-halo-color': 'rgba(0,0,0,0.9)',
        'text-halo-width': 1.2,
      },
    });
  }

  if (!map.getLayer(EXTERNAL_TRACK_LABEL_LAYER)) {
    map.addLayer({
      id: EXTERNAL_TRACK_LABEL_LAYER,
      type: 'symbol',
      source: EXTERNAL_TRACK_SOURCE_ID,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-letter-spacing': 0.08,
        // Below the ring, where a supply site's name sits above its own, so a
        // contact sharing a coordinate with one does not smear into it.
        'text-offset': [0, 1.9],
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-opacity': ['get', 'opacity'],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  // The lifecycle line, on the symbol rather than in a panel. A staleness an
  // operator has to open a tab to discover is a staleness they will not see.
  if (!map.getLayer(EXTERNAL_TRACK_STATUS_LAYER)) {
    map.addLayer({
      id: EXTERNAL_TRACK_STATUS_LAYER,
      type: 'symbol',
      source: EXTERNAL_TRACK_SOURCE_ID,
      layout: {
        'text-field': ['get', 'status'],
        'text-size': 8.5,
        'text-letter-spacing': 0.06,
        'text-offset': [0, 3.2],
      },
      paint: {
        'text-color': ['get', 'statusColor'],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  const draw = (contacts: ExternalContact[]): void => {
    const source = geoJsonSource(map, EXTERNAL_TRACK_SOURCE_ID);
    if (!source) return;
    const now = clock();
    const shown = contacts.filter(visibleKind);
    source.setData({
      type: 'FeatureCollection',
      features: shown.map((contact) => {
        const feature = externalContactFeature(contact, now) as {
          properties: Record<string, unknown>;
        };
        // The hover lines ride along as JSON so the label can be built from the
        // hit-test alone, without a second lookup into a store the pointer
        // handler would have to keep in sync.
        feature.properties.lines = JSON.stringify(externalHoverLines(contact, now));
        return feature;
      }),
    });
    // What is on the map, not what arrived. A count that ignored the filter
    // would report contacts the operator has switched off as drawn.
    drawn = shown.length;
    redraws += 1;
  };

  const unsubscribe = subscribeToExternalPicture(draw);

  const onHoverMove = (event: HoverEvent): void => {
    const properties = event.features?.[0]?.properties;
    if (!properties) return;
    options.onHover?.(hoverFromProperties(properties, event.point));
  };
  const onHoverLeave = (): void => options.onHover?.(null);

  const unbindHover = bindHoverLayer(map, EXTERNAL_TRACK_HALO_LAYER, onHoverMove, onHoverLeave);

  return {
    /**
     * Redraw from the contacts already held, for when a LAYERS switch changes.
     * Without it the filter would only take effect on the next poll, so the
     * map would keep showing what the operator just switched off for as long as
     * a poll interval — long enough to read as the switch not working.
     */
    redraw: () => draw(useExternalStore.getState().contacts),
    destroy: () => {
      unsubscribe();
      unbindHover();
      // A label left behind would hang over a map that no longer has the layer.
      options.onHover?.(null);
      for (const id of [
        EXTERNAL_TRACK_STATUS_LAYER,
        EXTERNAL_TRACK_LABEL_LAYER,
        EXTERNAL_TRACK_SYMBOL_LAYER,
        EXTERNAL_TRACK_HALO_LAYER,
        EXTERNAL_TRACK_FRAME_LAYER,
      ]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(EXTERNAL_TRACK_SOURCE_ID)) map.removeSource(EXTERNAL_TRACK_SOURCE_ID);
    },
    stats: () => ({ redraws, contacts: drawn }),
  };
}

/* The hovered contact, published outside React — see useCommandTargetLayer for
 * why the layer is mounted by the screen and the label subscribes separately. */
const hoverListeners = new Set<(hover: ExternalTrackHover | null) => void>();

function publishHover(hover: ExternalTrackHover | null): void {
  for (const listener of hoverListeners) listener(hover);
}

/**
 * How often the external picture is re-fetched.
 *
 * Slower than telemetry on purpose. These are other people's tracks arriving
 * over other people's links, and polling faster than they publish only spends
 * the box's budget to redraw the same contacts.
 */
export const EXTERNAL_POLL_MS = 4000;

/**
 * Mount the layer, and keep the picture fed.
 *
 * The poll lives with the layer rather than in a screen for the reason recorded
 * on Command's mission fetch: a layer that renders data somebody else was
 * supposed to load draws nothing at all on a screen that was opened directly.
 * Sources are polled alongside tracks because the feed's liveness is what
 * decides what every one of its contacts means.
 */
export function useExternalTrackLayer(): void {
  const controller = useMapController();
  const layerRef = useRef<ExternalTrackLayer | null>(null);
  const adsb = useMapStore((state) => state.layers.adsb);
  const cot = useMapStore((state) => state.layers.cot);

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createExternalTrackLayer(map, {
          onHover: (hover) => {
            const raw = controller.raw();
            if (raw) raw.getCanvas().style.cursor = hover ? 'pointer' : '';
            publishHover(hover);
          },
        });
        layerRef.current = layer;
        return () => {
          layerRef.current = null;
          layer.destroy();
        };
      }),
    [controller],
  );

  // The filter itself reads the store at draw time; this only says "draw again
  // now" so the switch is felt immediately rather than at the next poll.
  useEffect(() => {
    layerRef.current?.redraw();
  }, [adsb, cot]);

  useEffect(() => {
    // Sources first: a track's meaning depends on whether its feed is alive, so
    // classifying a fresh batch against last cycle's health would spend one
    // whole poll reading a dead feed's contacts as merely stale.
    const load = (): void => {
      void externalActions.loadSources().then(() => externalActions.loadTracks());
    };
    load();
    const timer = setInterval(load, EXTERNAL_POLL_MS);
    return () => clearInterval(timer);
  }, []);
}

/** Roughly the label's width, for keeping it inside the viewport. */
const LABEL_WIDTH = 260;

/** The hover label for whatever external contact the pointer is over. */
export function ExternalTrackHoverLabel(): ReactNode {
  const [hover, setHover] = useState<ExternalTrackHover | null>(null);

  useEffect(() => {
    hoverListeners.add(setHover);
    return () => {
      hoverListeners.delete(setHover);
    };
  }, []);

  if (!hover) return null;

  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const left = Math.min(hover.x + 14, viewportWidth - LABEL_WIDTH - 12);

  return (
    <div
      role="tooltip"
      className="glass pointer-events-none absolute z-40 rounded-[12px] px-3 py-2"
      style={{ left, top: hover.y + 14, maxWidth: LABEL_WIDTH }}
    >
      <span
        className="block font-mono text-[11px] font-semibold tracking-wide"
        style={{ color: hover.color }}
      >
        {hover.title}
      </span>
      {hover.lines.map((line) => (
        <span key={line} className="mt-0.5 block font-mono text-[9.5px] text-white/55">
          {line}
        </span>
      ))}
    </div>
  );
}

export default ExternalTrackHoverLabel;
